// Sufi AI deep panels: Parametric inverse design, DFM checker, FEA viewer, PPAP generator
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Wand2, CheckCircle2, XCircle, AlertTriangle, Download, FileText, Activity, Sliders } from "lucide-react";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { useNavigate } from "react-router-dom";

export function ParametricPanel({ accent, gradient }) {
  const nav = useNavigate();
  const [req, setReq] = useState("Lightweight bracket capable of holding 500 N with 4× safety factor for an electric vehicle motor mount");
  const [material, setMaterial] = useState("Al 6061-T6");
  const [objectives, setObjectives] = useState([
    { name: "Minimise mass", weight: 0.5 },
    { name: "Maximise stiffness", weight: 0.3 },
    { name: "Minimise cost", weight: 0.2 },
  ]);
  const [constraints, setConstraints] = useState("Fits within 120×80×60 mm envelope. Bolt pattern M8×4 at 60 mm spacing.");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const updateObj = (i, key, val) => {
    const c = [...objectives]; c[i] = { ...c[i], [key]: key === "weight" ? parseFloat(val) : val };
    setObjectives(c);
  };

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/sufi/parametric", { requirement: req, objectives, constraints, material });
      setResult(r.data);
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e?.response?.data?.detail || "Optimisation failed");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="sufi-parametric">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Inverse design</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><Sliders className="h-6 w-6" /> Parametric optimisation</h2>
        <div className="text-xs text-muted-foreground mt-1">Multi-objective design synthesis with weighted KPIs.</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-sm border border-border/60 bg-card p-4 space-y-3">
          <Field label="Requirement">
            <textarea value={req} onChange={(e) => setReq(e.target.value)} rows={3} data-testid="param-req"
              className="w-full rounded-sm border border-border bg-background p-3 text-sm resize-none" />
          </Field>
          <Field label="Material">
            <input value={material} onChange={(e) => setMaterial(e.target.value)} data-testid="param-mat"
              className="h-9 w-full rounded-sm border border-border bg-background px-3 text-sm" />
          </Field>
          <Field label="Objectives + weights">
            {objectives.map((o, i) => (
              <div key={i} className="flex gap-2 mb-1.5">
                <input value={o.name} onChange={(e) => updateObj(i, "name", e.target.value)} data-testid={`param-obj-name-${i}`}
                  className="flex-1 h-8 rounded-sm border border-border bg-background px-2 text-xs" />
                <input type="number" step="0.05" min="0" max="1" value={o.weight} onChange={(e) => updateObj(i, "weight", e.target.value)}
                  data-testid={`param-obj-weight-${i}`}
                  className="w-20 h-8 rounded-sm border border-border bg-background px-2 text-xs font-mono" />
              </div>
            ))}
          </Field>
          <Field label="Constraints">
            <textarea value={constraints} onChange={(e) => setConstraints(e.target.value)} rows={2} data-testid="param-constraints"
              className="w-full rounded-sm border border-border bg-background p-2 text-xs resize-none" />
          </Field>
          <button onClick={run} disabled={busy} data-testid="param-run"
            className="w-full h-10 rounded-sm text-white text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2"
            style={{ background: gradient }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Run optimisation
          </button>
        </div>
        <div className="rounded-sm border border-border/60 bg-card p-4 max-h-[600px] overflow-y-auto">
          {result ? (
            <div className="space-y-4">
              {result.geometry_b64 && (
                <div className="rounded-sm overflow-hidden border border-border/40">
                  <img src={`data:image/png;base64,${result.geometry_b64}`} alt="optimised geometry" className="w-full" />
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Rationale</div>
                <div className="text-sm">{result.rationale}</div>
              </div>
              {result.design_variables?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Design variables</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {result.design_variables.map((v, i) => (
                        <tr key={i} className="border-t border-border/40" data-testid={`param-var-${i}`}>
                          <td className="py-1">{v.name}</td>
                          <td className="py-1 font-mono">{v.value} {v.unit}</td>
                          <td className="py-1 text-muted-foreground text-[10px]">[{v.bounds?.join(" – ")}]</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {result.predicted_kpis?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Predicted KPIs</div>
                  <div className="grid grid-cols-2 gap-2">
                    {result.predicted_kpis.map((k, i) => (
                      <div key={i} className="rounded-sm border border-border/40 px-2 py-1.5" data-testid={`param-kpi-${i}`}>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.name}</div>
                        <div className="font-mono text-sm">{k.value} {k.unit}</div>
                        {k.delta_vs_target_pct != null && <div className="text-[10px]" style={{ color: k.delta_vs_target_pct >= 0 ? "#22c55e" : "#ef4444" }}>Δ {k.delta_vs_target_pct > 0 ? "+" : ""}{k.delta_vs_target_pct}%</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.trade_offs && (
                <div className="text-xs text-muted-foreground italic">Trade-offs: {result.trade_offs}</div>
              )}
            </div>
          ) : <div className="text-sm text-muted-foreground">Set objectives & weights then press Run.</div>}
        </div>
      </div>
    </div>
  );
}

export function DFMPanel({ accent, gradient }) {
  const [process, setProcess] = useState("injection_molding");
  const [geom, setGeom] = useState({});
  const [rules, setRules] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/sufi/dfm/rules/${process}`).then((r) => {
      setRules(r.data.rules || []);
      const defaults = {
        injection_molding: { wall_thickness_mm: 2.0, draft_deg: 1.0, boss_outer_to_inner: 2.5, rib_to_wall_ratio: 0.5, min_radius_mm: 1.0 },
        cnc_milling:        { min_internal_radius_mm: 1.0, depth_to_width_ratio: 3.0, wall_thickness_mm: 1.0, tolerance_mm: 0.05 },
        sheet_metal:        { bend_radius_mm: 2.0, hole_to_edge_mm: 3.0, flange_length_mm: 6.0 },
        additive:           { max_overhang_deg: 45, min_feature_mm: 0.5, max_bridge_mm: 4 },
      };
      setGeom(defaults[process] || {});
    });
  }, [process]);

  const check = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sufi/dfm/check", { process, geometry: geom });
      setResult(r.data);
    } catch (e) { toast.error("Check failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="sufi-dfm">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">DFM checks</div>
        <h2 className="font-serif text-3xl mt-1">Design-for-manufacturing</h2>
        <div className="text-xs text-muted-foreground mt-1">Deterministic rule-based checks for common manufacturing processes.</div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {["injection_molding", "cnc_milling", "sheet_metal", "additive"].map((p) => (
          <button key={p} onClick={() => setProcess(p)} data-testid={`dfm-proc-${p}`}
            className={`h-9 px-3 text-xs rounded-sm border transition-colors ${process === p ? "border-foreground bg-card" : "border-border/60 bg-card/40 text-muted-foreground"}`}>
            {p.replace(/_/g, " ")}
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-sm border border-border/60 bg-card p-4 space-y-2">
          <div className="text-sm font-medium">Geometry inputs</div>
          {rules.map((r) => (
            <div key={r.key}>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.name}</label>
              <input type="number" step="0.1" value={geom[r.key] || ""} onChange={(e) => setGeom({ ...geom, [r.key]: parseFloat(e.target.value) || 0 })}
                data-testid={`dfm-input-${r.key}`}
                className="mt-0.5 h-8 w-full rounded-sm border border-border bg-background px-2 text-sm font-mono" />
            </div>
          ))}
          <button onClick={check} disabled={busy} data-testid="dfm-check"
            className="w-full h-9 mt-2 rounded-sm text-white text-sm disabled:opacity-60" style={{ background: gradient }}>
            {busy ? "Checking…" : "Run DFM check"}
          </button>
        </div>
        <div className="rounded-sm border border-border/60 bg-card p-4">
          {result ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-3xl font-mono">{result.score}<span className="text-sm text-muted-foreground">/100</span></div>
                <div>
                  <div className="text-sm">{result.passed}/{result.total} checks passed</div>
                  <div className="text-xs text-muted-foreground">{result.summary}</div>
                </div>
              </div>
              <div className="space-y-1.5">
                {result.checks.map((c, i) => {
                  const Icon = c.status === "pass" ? CheckCircle2 : (c.status === "warn" ? AlertTriangle : XCircle);
                  const color = c.status === "pass" ? "#22c55e" : (c.status === "warn" ? "#f97316" : "#ef4444");
                  return (
                    <div key={i} className="flex items-start gap-2 text-sm" data-testid={`dfm-check-${i}`}>
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color }} />
                      <div>
                        <div><span className="font-medium">{c.name}:</span> <span className="font-mono text-xs">{c.value}</span></div>
                        {c.status !== "pass" && <div className="text-xs text-muted-foreground">{c.message}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : <div className="text-sm text-muted-foreground">Set geometry and run a check.</div>}
        </div>
      </div>
    </div>
  );
}

export function FEAPanel({ accent }) {
  const [w, setW] = useState(100), [h, setH] = useState(60), [load, setLoad] = useState(1500);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/sufi/fea/preview?plate_w=${w}&plate_h=${h}&load_n=${load}`);
      setData(r.data);
    } catch (e) { toast.error("FEA failed"); }
    setBusy(false);
  };

  // Color scale: blue (cool) → red (hot)
  const heatColor = (v, vmax) => {
    const t = Math.max(0, Math.min(1, v / Math.max(0.01, vmax)));
    const r = Math.round(255 * t);
    const b = Math.round(255 * (1 - t));
    return `rgb(${r}, 80, ${b})`;
  };

  return (
    <div className="space-y-4" data-testid="sufi-fea">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">FEA preview</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><Activity className="h-6 w-6" /> Stress field viewer</h2>
        <div className="text-xs text-muted-foreground mt-1">Cantilever plate with central hole. Synthetic preview — wire a real solver for production runs.</div>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <Field label="Width (mm)"><input type="number" value={w} onChange={(e) => setW(parseFloat(e.target.value))} data-testid="fea-w" className="h-9 w-24 rounded-sm border border-border bg-background px-2 text-sm font-mono" /></Field>
        <Field label="Height (mm)"><input type="number" value={h} onChange={(e) => setH(parseFloat(e.target.value))} data-testid="fea-h" className="h-9 w-24 rounded-sm border border-border bg-background px-2 text-sm font-mono" /></Field>
        <Field label="Load (N)"><input type="number" value={load} onChange={(e) => setLoad(parseFloat(e.target.value))} data-testid="fea-load" className="h-9 w-28 rounded-sm border border-border bg-background px-2 text-sm font-mono" /></Field>
        <button onClick={run} disabled={busy} data-testid="fea-run" className="h-9 px-4 rounded-sm text-white text-sm" style={{ background: accent }}>{busy ? "…" : "Compute"}</button>
      </div>
      {data && (
        <div className="rounded-sm border border-border/60 bg-card p-4">
          <div className="flex items-center justify-between mb-2 text-xs">
            <div>Max stress: <span className="font-mono">{data.max_stress_mpa} MPa</span></div>
            {data.yield_warning && <div className="text-orange-500">⚠ exceeds typical 6061-T6 yield (240 MPa)</div>}
          </div>
          <div className="overflow-x-auto bg-[#0a0e17] p-2 rounded-sm">
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${data.nx}, 1fr)`, gap: "0px", aspectRatio: `${data.nx}/${data.ny}` }} data-testid="fea-grid">
              {data.stress_mpa_grid.flat().map((v, i) => (
                <div key={i} style={{ background: heatColor(v, data.max_stress_mpa), aspectRatio: "1/1" }} title={`${v} MPa`} />
              ))}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="font-mono">0</span>
            <div className="flex-1 h-2 rounded-sm" style={{ background: "linear-gradient(to right, rgb(0,80,255), rgb(255,80,0))" }} />
            <span className="font-mono">{data.max_stress_mpa} MPa</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function PPAPPanel({ accent, gradient }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ part_name: "Motor mount bracket", part_number: "BRK-1001-A", customer: "Aramco", revision: "A" });
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sufi/ppap", form);
      setLast(r.data);
      toast.success("PPAP packet ready");
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e?.response?.data?.detail || "PPAP failed");
    }
    setBusy(false);
  };

  const download = (id) => {
    const token = localStorage.getItem("sanaa_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sufi/ppap/${id}/docx`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(b);
        a.download = `PPAP_${form.part_number}.docx`; a.click();
      });
  };

  return (
    <div className="space-y-4" data-testid="sufi-ppap">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">PPAP</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><FileText className="h-6 w-6" /> AIAG-compliant PPAP packet</h2>
        <div className="text-xs text-muted-foreground mt-1">Generates an 18-section Level-3 PPAP submission packet as a Word document.</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-sm border border-border/60 bg-card p-4 space-y-3">
          {["part_name", "part_number", "customer", "revision"].map((k) => (
            <Field key={k} label={k.replace(/_/g, " ")}>
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`ppap-${k}`}
                className="h-9 w-full rounded-sm border border-border bg-background px-3 text-sm" />
            </Field>
          ))}
          <Field label="Process description">
            <textarea value={form.process_description || ""} onChange={(e) => setForm({ ...form, process_description: e.target.value })} rows={3} data-testid="ppap-process"
              className="w-full rounded-sm border border-border bg-background p-2 text-sm resize-none" />
          </Field>
          <button onClick={generate} disabled={busy} data-testid="ppap-generate"
            className="w-full h-10 rounded-sm text-white text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2"
            style={{ background: gradient }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generate PPAP packet
          </button>
        </div>
        <div className="rounded-sm border border-border/60 bg-card p-4">
          {last ? (
            <>
              <div className="font-medium">PPAP packet ready</div>
              <div className="text-sm text-muted-foreground">{last.part_name} ({last.part_number})</div>
              <button onClick={() => download(last.id)} data-testid="ppap-download"
                className="mt-3 inline-flex h-9 items-center gap-2 px-3 rounded-sm border border-border hover:bg-secondary text-sm">
                <Download className="h-4 w-4" /> Download DOCX
              </button>
              <div className="mt-3 text-xs text-muted-foreground">18 sections per AIAG 4th Edition · Level 3.</div>
            </>
          ) : <div className="text-sm text-muted-foreground">Fill the form and generate.</div>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-[0.16em] text-muted-foreground capitalize">{label}</span><div className="mt-1.5">{children}</div></label>;
}
